import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ILightboxImage {
    src: string;
    alt: string;
}

const ProductSection: React.FC = () => {
    const { t } = useTranslation();
    const [zoomed, setZoomed] = useState<ILightboxImage | null>(null);

    const closeZoom = useCallback(() => setZoomed(null), []);

    useEffect(() => {
        if (!zoomed) return;

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') closeZoom();
        };

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', onKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [zoomed, closeZoom]);

    const cards = [
        {
            image: '/images/panel3.jpg',
            imageAlt: 'When memory speaks, OSMIA listens',
            hoverColor: 'group-hover:text-blue-600',
            dotColor: 'bg-blue-500',
            titleKey: 'product.card1.title',
            descKey: 'product.card1.desc',
            features: [
                t('product.card1.feature1'),
                t('product.card1.feature2'),
                t('product.card1.feature3'),
            ],
        },
        {
            image: '/images/panel5.jpg',
            imageAlt: 'Transforming cognitive health management with AI-powered insights',
            hoverColor: 'group-hover:text-teal-600',
            dotColor: 'bg-teal-500',
            titleKey: 'product.card3.title',
            descKey: 'product.card3.desc',
            features: [
                t('product.card3.feature1'),
                t('product.card3.feature2'),
                t('product.card3.feature3'),
            ],
        },
    ];

    const steps = [
        { image: '/images/guide1.png', num: t('product.steps.s1_num'), title: t('product.steps.s1_title'), desc: t('product.steps.s1_desc') },
        { image: '/images/guide2.png', num: t('product.steps.s2_num'), title: t('product.steps.s2_title'), desc: t('product.steps.s2_desc') },
        { image: '/images/guide3.png', num: t('product.steps.s3_num'), title: t('product.steps.s3_title'), desc: t('product.steps.s3_desc') },
        { image: '/images/guide4.png', num: t('product.steps.s4_num'), title: t('product.steps.s4_title'), desc: t('product.steps.s4_desc') },
    ];

    return (
        <section id="product" className="bg-white">

            {/* 블록 1 — 제품 소개 (흰색) */}
            <div className="py-10 md:py-16">
              <div className="container mx-auto px-6">
    {/* 텍스트(왼쪽)와 이미지(오른쪽)를 나란히 배치하기 위한 Grid 컨테이너 */}
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.3fr] gap-8 lg:gap-14 items-center mb-10 md:mb-16">
        
        {/* 1. 텍스트 영역 (왼쪽) */}
        {/* 기존의 text-center와 mx-auto를 제거하여 왼쪽 정렬되도록 수정했습니다. */}
        <div>
            <h4 className="text-blue-600 font-bold uppercase tracking-widest mb-2 text-2xl md:text-base">
                {t('product.label')}
            </h4>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-2 md:mb-3 tracking-tight">
                OSMIA PRO
            </h2>
            <p className="text-blue-700 font-bold text-lg md:text-2xl mb-4 md:mb-6">
                {t('product.tagline')}
            </p>
            <p className="text-gray-500 text-[14px] md:text-lg leading-relaxed">
                {t('product.desc')}
            </p>
        </div>

        {/* 2. 메인 이미지 영역 (오른쪽) */}
        <div className="flex justify-center lg:justify-end">
            <img
                src="/images/panel2.jpg"
                alt="Awaken the senses, bring memories to life"
                /* 모바일에서는 꽉 차게, PC에서는 유연하게 조정되도록 w-full로 변경 */
                className="w-full max-w-full h-auto max-h-[60vh] rounded-2xl shadow-lg object-contain"
            />
        </div>
        
    </div>
              </div>
            </div>

            {/* 블록 2 — 핵심 기술 (연회색) */}
            <div className="bg-gray-50 py-10 md:py-16">
              <div className="container mx-auto px-6">
                <div className="space-y-12 md:space-y-20">
                    {cards.map((card, index) => (
                        <div
                            key={index}
                            className="grid grid-cols-1 lg:grid-cols-[1.35fr_1fr] gap-6 lg:gap-14 items-center border-t border-gray-200 pt-12 md:pt-16 first:border-t-0 first:pt-0 lg:border-t-0 lg:pt-0"
                        >
                            {/* 라벨 — 세로 배치에서는 사진 위에 (PC는 아래 설명 칼럼 안에) */}
                            {index === 0 && (
                                <h4 className="lg:hidden text-blue-600 font-bold uppercase tracking-widest text-2xl md:text-base">
                                    CORE TECHNOLOGY
                                </h4>
                            )}

                            {/* 사진 */}
                            <button
                                type="button"
                                onClick={() => setZoomed({ src: card.image, alt: card.imageAlt })}
                                aria-label={`${card.imageAlt} — ${t('product.zoom_hint')}`}
                                className="group block w-full rounded-2xl overflow-hidden border border-gray-200 shadow-lg hover:shadow-2xl transition-shadow duration-300 cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                            >
                                <img
                                    src={card.image}
                                    alt={card.imageAlt}
                                    className="w-full h-auto block transition-transform duration-500 group-hover:scale-[1.02]"
                                />
                            </button>

                            {/* 설명 */}
                            <div className="order-2 lg:order-1 flex flex-col justify-center">
                                {index === 0 && (
                                    <h4 className="hidden lg:block text-blue-600 font-bold uppercase tracking-widest text-2xl md:text-base mb-2 md:mb-3">
                                        CORE TECHNOLOGY
                                    </h4>
                                )}
                                <h3 className="text-2xl md:text-4xl font-black text-gray-900 mb-4 md:mb-5 leading-tight tracking-tight">
                                    {t(card.titleKey)}
                                </h3>
                                <p className="text-gray-600 text-base md:text-xl mb-6 md:mb-8 leading-relaxed">
                                    {t(card.descKey)}
                                </p>
                                <ul className="text-[15px] md:text-lg text-gray-500 space-y-3 md:space-y-4">
                                    {card.features.map((feature, fi) => (
                                        <li key={fi} className="flex items-start">
                                            <span className={`w-2.5 h-2.5 ${card.dotColor} rounded-full mr-3 mt-[0.5rem] flex-shrink-0`}></span>
                                            <span className="leading-relaxed">{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    ))}
                </div>
              </div>
            </div>

            {/* 블록 3 — 사용 순서 (흰색) */}
            <div className="py-10 md:py-16">
              <div className="container mx-auto px-6">
                <div>
                    <div className="max-w-3xl mb-8 md:mb-12">
                        <h4 className="text-blue-600 font-bold uppercase tracking-widest text-2xl md:text-base mb-2 md:mb-3">
                            HOW IT WORKS
                        </h4>
                        <h3 className="text-2xl md:text-4xl font-black text-gray-900 mb-2 md:mb-3 leading-tight tracking-tight">
                            {t('product.card2.title')}
                        </h3>
                        {/* 여기에 whitespace-nowrap을 추가했습니다 */}
    <p className="text-blue-700 font-bold text-lg md:text-2xl mb-4 md:mb-5 whitespace-nowrap">
        {t('product.steps.subtitle')}
    </p>
                        <p className="text-gray-600 text-base md:text-xl leading-relaxed">
                            {t('product.card2.desc')}
                        </p>
                    </div>

                    <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                        {steps.map((step) => (
                            <li key={step.num} className="flex flex-col">
                                <button
                                    type="button"
                                    onClick={() => setZoomed({ src: step.image, alt: `${step.num} ${step.title}` })}
                                    aria-label={`${step.title} — ${t('product.zoom_hint')}`}
                                    className="group block w-full aspect-[548/281] rounded-xl overflow-hidden bg-gray-50 border border-gray-200 shadow-md hover:shadow-xl transition-shadow duration-300 cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 mb-4"
                                >
                                    <img
                                        src={step.image}
                                        alt={step.title}
                                        loading="lazy"
                                        className="w-full h-full block object-contain transition-transform duration-500 group-hover:scale-[1.03]"
                                    />
                                </button>

                                <div className="flex items-baseline gap-2 mb-1.5 whitespace-nowrap">
                                    <span className="text-blue-600 font-black text-lg md:text-xl tabular-nums">
                                        {step.num}
                                    </span>
                                    <h4 className="font-bold text-gray-900 text-base md:text-lg leading-none">
                                        {step.title}
                                    </h4>
                                </div>
                                <p className="text-gray-500 text-[14px] md:text-[15px] leading-relaxed">
                                    {step.desc}
                                </p>
                            </li>
                        ))}
                    </ol>
                </div>
              </div>
            </div>

            {zoomed && (
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-label={zoomed.alt}
                    onClick={closeZoom}
                    className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4 md:p-8 cursor-zoom-out animate-[fadeIn_150ms_ease-out]"
                >
                    <button
                        type="button"
                        onClick={closeZoom}
                        aria-label={t('product.zoom_close')}
                        className="absolute top-4 right-4 md:top-6 md:right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/25 text-white text-2xl leading-none flex items-center justify-center transition-colors"
                    >
                        ×
                    </button>
                    <img
                        src={zoomed.src}
                        alt={zoomed.alt}
                        onClick={(e) => e.stopPropagation()}
                        className="max-w-full max-h-full w-auto h-auto object-contain rounded-lg shadow-2xl cursor-default"
                    />
                </div>
            )}
        </section>
    );
};

export default ProductSection;
